import { ref } from 'vue'

const baseUri = process.env.VUE_APP_API_URL

const SSE_RECONNECT_DELAY = 3000
const SSE_MAX_RETRIES = 5

export function useSSE(endpoint) {
    const isConnected = ref(false)
    const error = ref(null)
    const lastEvent = ref(null)
    const retryCount = ref(0)
    const authFailed = ref(false)
    const lastSequenceId = ref(null)

    let eventSource = null
    let reconnectTimeout = null
    const eventListeners = new Map()

    const connect = () => {
        if (eventSource) {
            return
        }

        // Don't attempt to connect if we've had an auth failure
        if (authFailed.value) {
            return
        }

        error.value = null

        // Build URL with lastSequenceId for replay on reconnect
        let url = `${baseUri}/${endpoint}`
        if (lastSequenceId.value !== null) {
            url += `?lastSequenceId=${lastSequenceId.value}`
        }

        try {
            eventSource = new EventSource(url, { withCredentials: true })

            // Attach event listeners IMMEDIATELY after creating EventSource
            // This ensures we don't miss any events that arrive before onopen fires
            // (EventSource can receive events while still in CONNECTING state)
            for (const [eventType, callback] of eventListeners) {
                if (eventType === '__dataHandlers' || eventType === '__messageHandler') {
                    continue
                }
                eventSource.addEventListener(eventType, callback)
            }

            // Attach the message handler if it exists
            const messageHandler = eventListeners.get('__messageHandler')
            if (messageHandler) {
                eventSource.onmessage = messageHandler
            }

            eventSource.onopen = () => {
                isConnected.value = true
                error.value = null
                retryCount.value = 0
                authFailed.value = false
            }

            eventSource.onerror = (e) => {
                isConnected.value = false

                // EventSource doesn't expose HTTP status codes directly,
                // but if the connection closes immediately after opening attempt,
                // it's likely an auth failure (401/403)
                if (eventSource.readyState === EventSource.CLOSED) {
                    // Check if we never successfully connected (auth failure likely)
                    if (retryCount.value === 0 && !isConnected.value) {
                        error.value = 'Authentication failed'
                        authFailed.value = true
                        eventSource = null
                        return
                    }

                    error.value = 'Connection lost'
                    eventSource = null
                    scheduleReconnect()
                }
            }
        } catch (e) {
            error.value = e.message
            scheduleReconnect()
        }
    }

    const scheduleReconnect = () => {
        if (retryCount.value >= SSE_MAX_RETRIES) {
            error.value = 'Max retries exceeded'
            return
        }

        retryCount.value++
        const delay = SSE_RECONNECT_DELAY * retryCount.value

        reconnectTimeout = setTimeout(() => {
            connect()
        }, delay)
    }

    const disconnect = () => {
        if (reconnectTimeout) {
            clearTimeout(reconnectTimeout)
            reconnectTimeout = null
        }

        if (eventSource) {
            eventSource.close()
            eventSource = null
        }

        isConnected.value = false
        retryCount.value = 0
        authFailed.value = false // Reset so next login can try again
        // Note: We intentionally preserve lastSequenceId so reconnects can resume
    }

    const resetSequence = () => {
        lastSequenceId.value = null
    }

    const updateSequenceId = (sequenceId) => {
        if (sequenceId !== undefined && sequenceId !== null) {
            lastSequenceId.value = sequenceId
        }
    }

    const addEventListener = (eventType, callback) => {
        const handler = (e) => {
            try {
                const data = JSON.parse(e.data)

                // Track sequence ID from any message that has one
                if (data.sequenceId !== undefined) {
                    updateSequenceId(data.sequenceId)
                }

                lastEvent.value = { type: eventType, data, timestamp: new Date() }
                callback(data)
            } catch (err) {
                console.error('Failed to parse SSE event data:', err)
            }
        }

        eventListeners.set(eventType, handler)

        // Always add to eventSource if it exists - including during the connection phase
        if (eventSource && eventSource.readyState !== EventSource.CLOSED) {
            eventSource.addEventListener(eventType, handler)
        }
    }

    // Listen for generic 'message' events and dispatch based on data.eventName
    const addDataEventListener = (eventName, callback) => {
        // Store callback in a map for dispatch
        if (!eventListeners.has('__dataHandlers')) {
            eventListeners.set('__dataHandlers', new Map())

            // Set up the generic message handler once
            const messageHandler = (e) => {
                try {
                    const wrapper = JSON.parse(e.data)

                    // Track sequence ID from any message that has one
                    if (wrapper.data?.sequenceId !== undefined) {
                        updateSequenceId(wrapper.data.sequenceId)
                    }

                    const handlers = eventListeners.get('__dataHandlers')
                    const handler = handlers?.get(wrapper.eventName)
                    if (handler) {
                        lastEvent.value = { type: wrapper.eventName, data: wrapper.data, timestamp: new Date() }
                        handler(wrapper.data)
                    }
                } catch (err) {
                    console.error('Failed to parse SSE message data:', err)
                }
            }

            eventListeners.set('__messageHandler', messageHandler)
            if (eventSource) {
                eventSource.onmessage = messageHandler
            }
        }

        eventListeners.get('__dataHandlers').set(eventName, callback)
    }

    const removeEventListener = (eventType) => {
        const handler = eventListeners.get(eventType)
        if (handler && eventSource) {
            eventSource.removeEventListener(eventType, handler)
        }
        eventListeners.delete(eventType)
    }

    return {
        isConnected,
        error,
        lastEvent,
        retryCount,
        authFailed,
        lastSequenceId,
        connect,
        disconnect,
        resetSequence,
        addEventListener,
        addDataEventListener,
        removeEventListener
    }
}
